import { groupBy, isUndefined, cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { PartialDeep } from 'type-fest';
import {
    CommandType,
    IUpdateCommand,
    IDeleteCommand,
    TBlock,
    TCommand,
    ICreateCommand,
    IBaseFlowContext,
    DataHandleMethod,
    IAttributionContext,
    TBlockDataForServer
} from '../types/builder.types';
import { Page, PageDataForServer } from '../models/page.model';
import {
    Action,
    ActionBaseDataForServer
} from '../services/actions-manager/action';
import { ContainerableBlock } from '../models/blocks/containerable-block.model';
import { Flow } from '../models/flow.model';
import {
    ITransformedPageResponse,
    IVariableResponse,
    TBlockResponse
} from '../types/server.types';
import { cloneClass } from './data.utils';
import { ActionName } from '../services/actions-manager/action-name';
import { ACTION_TYPE_TO_SOURCE_TYPE_MAP } from '~/utils/block.utils';
import { BaseBlock } from '~/models/blocks/base-block.model';

export function extractOldData(data: object, item: Record<string, any>) {
    const oldData = {};

    Object.keys(data).forEach(dataKey => {
        const value = cloneDeep(item[dataKey]);

        oldData[dataKey] = !isUndefined(value) ? value : null; // Only if the value is undefined the object destructor for the server data build will remove the property, be aware that if it suppose to be an empty string (for cx editor) you must put it yourself.
    });
    return oldData;
}

export function commandsByType(commands: TCommand[]) {
    const groupedCommands = groupBy(commands, cmd => cmd.type);

    return {
        createCommands:
            (groupedCommands[CommandType.Create] as ICreateCommand[]) || [],
        updateCommands:
            (groupedCommands[CommandType.Update] as IUpdateCommand[]) || [],
        deleteCommands:
            (groupedCommands[CommandType.Delete] as IDeleteCommand[]) || []
    };
}

function transformData(
    data: Record<string, any>,
    transformDictionary: Record<string, string>
) {
    const dataCopy = { ...data };
    const tempData = <Partial<ContainerableBlock>>dataCopy;
    const transformedData: Record<string, any> = {};

    Object.keys(transformDictionary).forEach(fromProperty => {
        const toProperty = transformDictionary[fromProperty];
        transformedData[toProperty] = tempData[fromProperty];

        if (tempData[fromProperty]) {
            delete tempData[fromProperty];
        }
    });

    return {
        ...tempData,
        ...transformedData
    };
}

export interface VariableActionBaseDataForServer
    extends ActionBaseDataForServer {
    updated_variables?: Partial<IVariableResponse>[];
}

export function calculateVariablesBaseDataForServer(
    commands: TCommand[],
    action: Action<IBaseFlowContext>
): VariableActionBaseDataForServer {
    const { updateCommands } = commandsByType(commands);

    return {
        ...action.getBasicDataForServer(),
        updated_variables: updateCommands.map(cmd => ({
            ...(cmd.data as Partial<IVariableResponse>)
        }))
    };
}

const addAttributionCommandData = (
    commandDataForServer: TBlock,
    actionName: string
) => {
    const data = commandDataForServer.getDataForServer();

    if (commandDataForServer.attributable) {
        data.source_type = ACTION_TYPE_TO_SOURCE_TYPE_MAP[actionName];
    }

    return data;
};

export interface BlockActionBaseDataForServer extends ActionBaseDataForServer {
    page_id?: string;
    created_blocks?: Partial<TBlockDataForServer>[];
    updated_blocks?: Partial<TBlockResponse>[];
    deleted_blocks?: Partial<Pick<BaseBlock, 'block_id' | 'page_id'>>[];
}

export function calculateBlockBaseDataForServer(
    commands: TCommand[],
    action: Action<IBaseFlowContext>
): BlockActionBaseDataForServer {
    const {
        ctx: { pageId }
    } = action;

    const { createCommands, updateCommands, deleteCommands } =
        commandsByType(commands);

    return {
        ...action.getBasicDataForServer(),
        page_id: pageId,
        created_blocks:
            createCommands && createCommands.length
                ? createCommands.map(cmd =>
                      addAttributionCommandData(cmd.data as TBlock, action.name)
                  )
                : undefined,
        updated_blocks:
            updateCommands && updateCommands.length
                ? updateCommands.map(
                      ({ data }) => data as Partial<TBlockResponse>
                  )
                : undefined,
        deleted_blocks:
            deleteCommands && deleteCommands.length
                ? deleteCommands.map(cmd => cmd.data)
                : undefined
    };
}

export function calculatePageWithAttributionDataForServer(
    commands: TCommand[],
    action: Action<IAttributionContext>
) {
    const dataForServer = calculatePageBaseDataForServer(
        action.ctx.commands,
        action
    );
    const { source_block_id, source_page_id, source_flow_id } = action.ctx;

    return {
        ...dataForServer,
        source_flow_id,
        source_page_id,
        source_block_id
    };
}

export interface PageActionBaseDataForServer
    extends BlockActionBaseDataForServer,
        VariableActionBaseDataForServer {
    created_pages?: Partial<PageDataForServer>[];
    updated_pages?: Partial<ITransformedPageResponse>[];
    deleted_pages?: Pick<Page, 'page_id'>[];
}

export function calculatePageBaseDataForServer(
    commands: TCommand[],
    action: Action<IBaseFlowContext>
): PageActionBaseDataForServer {
    const { pageCommands, blockCommands, variableCommands } = groupBy(
        commands,
        ({ data }) => {
            if ((<Partial<TBlock>>data).block_id) {
                return 'blockCommands';
            } else if ((<Partial<IVariableResponse>>data).variable_id) {
                return 'variableCommands';
            } else {
                return 'pageCommands';
            }
        }
    );

    const blockBaseDataForServer = calculateBlockBaseDataForServer(
        blockCommands,
        action
    );

    const variableBaseDataForServer = calculateVariablesBaseDataForServer(
        variableCommands,
        action
    );

    const {
        createCommands: createdPageCommands,
        updateCommands: updatedPageCommands,
        deleteCommands: deletePageCommands
    } = commandsByType(pageCommands);

    return {
        ...blockBaseDataForServer,
        ...variableBaseDataForServer,
        created_pages:
            createdPageCommands && createdPageCommands.length
                ? createdPageCommands.map(({ data: page }) =>
                      (page as Page).getDataForServer()
                  )
                : undefined,
        updated_pages:
            updatedPageCommands && updatedPageCommands.length
                ? updatedPageCommands.map(({ data }) => data as Partial<Page>)
                : undefined,
        deleted_pages:
            deletePageCommands && deletePageCommands.length
                ? deletePageCommands.map(
                      ({ data }) => data as Pick<Page, 'page_id'>
                  )
                : undefined
    };
}

const transformFlowDataDictionary = {
    _theme: 'theme'
};

export function calculateFlowBaseDataForServer(
    commands: TCommand[],
    action: Action<IBaseFlowContext>
) {
    const { updateCommands } = commandsByType(commands);
    const flowUpdateChannel =
        action.name === ActionName.UpdateFlowContent
            ? 'flow_content_updates'
            : 'flow_updates';

    return {
        ...action.getBasicDataForServer(),
        [flowUpdateChannel]: updateCommands.map(({ data }) =>
            transformData(data, transformFlowDataDictionary)
        )
    };
}

export const createUpdateVariableValueCommand = (
    variableId: string,
    data: Partial<IVariableResponse>
): IUpdateCommand => ({
    id: uuidv4(),
    type: CommandType.Update,
    data: {
        variable_id: variableId,
        value: {
            ...data
        }
    }
});

export const createCreateBlockCommand = (data: TBlock): ICreateCommand => ({
    id: uuidv4(),
    type: CommandType.Create,
    data: data.clone() // TODO: Nirel was suppose to be cloneDeepClass but we have circular references.
});

interface IUpdateCommandsOptions {
    dataHandleMethod?: DataHandleMethod;
}

const UpdateCommandsOptionsDefaults = {
    dataHandleMethod: DataHandleMethod.merge
};

export const createUpdateBlockCommand = (
    blockId: string,
    data: Partial<TBlock>,
    { dataHandleMethod }: IUpdateCommandsOptions = UpdateCommandsOptionsDefaults
): IUpdateCommand => ({
    id: uuidv4(),
    type: CommandType.Update,
    data: {
        block_id: blockId,
        ...data
    },
    dataHandleMethod
});

export const createDeleteBlockCommand = (blockId: string): IDeleteCommand => ({
    id: uuidv4(),
    type: CommandType.Delete,
    data: {
        block_id: blockId
    }
});

export const createCreatePageCommand = (data: Page): ICreateCommand => ({
    id: uuidv4(),
    type: CommandType.Create,
    data: cloneClass(data)
});

export const createUpdatePageCommand = (
    pageId: string,
    data: Partial<Page>,
    { dataHandleMethod }: IUpdateCommandsOptions = UpdateCommandsOptionsDefaults
): IUpdateCommand => ({
    id: uuidv4(),
    type: CommandType.Update,
    data: {
        page_id: pageId,
        ...data
    },
    dataHandleMethod
});

export const createDeletePageCommand = (pageId: string): IDeleteCommand => ({
    id: uuidv4(),
    type: CommandType.Delete,
    data: {
        page_id: pageId
    }
});

export const createUpdateFlowCommand = (
    flowId: string,
    data: PartialDeep<Flow>,
    { dataHandleMethod }: IUpdateCommandsOptions = UpdateCommandsOptionsDefaults
): IUpdateCommand => ({
    id: uuidv4(),
    type: CommandType.Update,
    data: {
        _id: flowId,
        ...data
    },
    dataHandleMethod
});

export enum CommandsRelation {
    override,
    accumulate
}

export const filterUpdateCommandsForDeletedBlocks = (commands: TCommand[]) => {
    const deletedBlocksIds = commands.reduce((resultIds, command) => {
        if (command.type === CommandType.Delete) {
            const blockId = (command as IDeleteCommand).data.block_id;
            if (!!blockId) {
                resultIds[blockId] = true;
            }
        }

        return resultIds;
    }, {});

    if (!Object.keys(deletedBlocksIds).length) {
        return commands;
    }

    return commands.filter(command => {
        const blockCommand = command as IUpdateCommand;
        const blockData = blockCommand.data as Partial<ContainerableBlock>;
        const blockId = blockData.block_id;
        const isAlteringDeletedBlock = deletedBlocksIds[blockId];

        // Alter children that reference a deleted block
        if (blockData.children && blockData.children.length) {
            blockData.children = blockData.children.filter(
                x => !deletedBlocksIds[x.block_id]
            );
        }

        // We want to remove every command that reference to deleted block
        return command.type === CommandType.Delete || !isAlteringDeletedBlock;
    });
};

export const mergeCommands = (
    commands: TCommand[],
    currentCommands: TCommand[],
    commandsRelation: CommandsRelation
) => {
    if (!commands) {
        return currentCommands;
    }

    if (!currentCommands) {
        return commands;
    }

    const newCommands =
        commandsRelation === CommandsRelation.accumulate
            ? currentCommands.concat(commands)
            : commands;

    // Must verify that we don't alter blocks that are going to be deleted because we first apply the block deletion and then update
    return filterUpdateCommandsForDeletedBlocks(newCommands);
};
